import _ from 'lodash'
import { Component } from '@wix/thunderbolt-becky-types'
import { envRefs } from '@wix/thunderbolt-catharsis'
import { FeatureRefs } from '../cssFeatures.types'
import { StickyTopOffset } from './responsiveLayout.types'
import { createCssCompNode } from '../cssCompNode'

export const LANDING_PAGE_TOP_OFFSET = { '--top-offset': `0px` }
const DEFAULT_BREAKPOINT = 'default'

const generateCss = (isStickyOrFixedHeader: boolean, headerHeight?: number) => ({
	'--top-offset': isStickyOrFixedHeader ? 'var(--header-height)' : '0px',
	...(!!headerHeight && { '--header-height': `${headerHeight}px` }),
})

const getChildrenStickyTopOffset = (comp: Component, refs: FeatureRefs<'responsiveLayout'>) => {
	return (comp.components || []).map(refs.stickyTopOffset)
}

export const isRefComponent = (comp: Component): boolean => {
	return Boolean(comp.componentType === 'RefComponent' && comp.components?.length)
}

const isHeaderWrapper = (children: Array<StickyTopOffset>) => children?.includes(true)

export const stickyTopOffset = createCssCompNode('responsiveLayout', 'stickyTopOffset', {
	getDependencies: (component: Component, refs: FeatureRefs<'responsiveLayout'>) => {
		const componentViewMode = envRefs.componentViewMode

		if (component.componentType === 'HeaderContainer') {
			return {
				componentViewMode,
				hasResponsiveLayout: refs.hasResponsiveLayout(component),
				isOneDocMigrated: envRefs.isOneDocMigrated,
				classicLayout: refs.classicLayout(component),
				pagesContainerClassicLayout: refs.classicLayout('PAGES_CONTAINER'),
			}
		}

		return {
			componentViewMode,
			refCompChildren: isRefComponent(component) ? getChildrenStickyTopOffset(component, refs) : null,
			breakpointsVariantsToLayouts: isRefComponent(component) ? refs.compLayouts(component) : null,
			hasResponsiveLayout: refs.hasResponsiveLayout(component),
		}
	},
	toViewItem: (component, data) => {
		const {
			hasResponsiveLayout,
			componentViewMode,
			isOneDocMigrated,
			classicLayout,
			pagesContainerClassicLayout,
		} = data
		if (componentViewMode === 'editor' || (!hasResponsiveLayout && component.componentType !== 'HeaderContainer')) {
			return null
		}

		if (hasResponsiveLayout) {
			if (component.componentType === 'HeaderSection') {
				return true
			}

			const { breakpointsVariantsToLayouts, refCompChildren } = data
			if (refCompChildren && isHeaderWrapper(refCompChildren) && breakpointsVariantsToLayouts) {
				return _.mapValues(breakpointsVariantsToLayouts, (variantsToLayouts) => {
					const layoutDataItems = _.flatten(Object.values(variantsToLayouts))
					const isStickyOrFixedHeader = layoutDataItems.some(
						(layoutItem) =>
							layoutItem.type === 'FixedItemLayout' ||
							('position' in layoutItem && layoutItem.position === 'sticky')
					)
					return generateCss(isStickyOrFixedHeader)
				})
			}

			return null
		}

		const isStickyHeader = isOneDocMigrated
			? !!(classicLayout?.itemLayout?.type === 'MasterPageItemLayout' && classicLayout.itemLayout.isFixed)
			: !!component.layout.fixedPosition
		const headerHeight = isOneDocMigrated
			? pagesContainerClassicLayout?.itemLayout?.type === 'MasterPageItemLayout' &&
			  pagesContainerClassicLayout.itemLayout.top?.type === 'px'
				? pagesContainerClassicLayout.itemLayout.top.value
				: 0
			: component.layout.height
		return { [DEFAULT_BREAKPOINT]: generateCss(isStickyHeader, headerHeight) }
	},
})
